import React, { useState } from 'react';
import EditIcon from '../../assets/EditIcon';
import { IStaffAllocation } from '../../type/staffAllocation';
import DeleteIcon from '../../assets/DeleteIcon';
import operationAPI from '../../api/operationApi';
import { toast } from 'react-toastify';
import DeleteConfirmation from './DeleteConfirmation';
import { translate } from '../../i18n';
import { IBusDetails } from '../../type/busDetails';
import useBgColorStore from '../../store/bgColor';
import EditIconDark from '../../assets/EditIconDark';
import DeleteIconDark from '../../assets/DeleteIconDark';

interface CrewPilotDetailsProps {
  openModal?: any;
  crewList: IStaffAllocation[];
  onOperationStaffIdChange?: (operationStaffId: number) => void;
  onStaffChange: () => void;
  crewVehicleList: IBusDetails[];
}

const CrewCard: React.FC<CrewPilotDetailsProps> = ({
  openModal,
  crewList,
  onOperationStaffIdChange,
  onStaffChange,
  crewVehicleList,
}) => {
  const [editModes, setEditModes] = useState<boolean[]>(
    crewList.map(() => false), // Initialize with all false (not in edit mode)
  );

  // Function to handle the "Edit" button click
  const toggleEditMode = (index: number) => {
    const updatedEditModes = [...editModes];
    updatedEditModes[index] = !updatedEditModes[index]; // Toggle the edit mode for the clicked card
    setEditModes(updatedEditModes);
  };

  // Function to handle checkbox state change
  const handleCheckboxChange = (
    index: number,
    field: 'need' | 'kkd' | 'preCheck',
  ) => {
    const updatedCrewList = [...crewList];
    if (editModes[index]) {
      updatedCrewList[index] = {
        ...updatedCrewList[index],
        [field]: !updatedCrewList[index][field], // Toggle the checkbox value
      };
      // Update the crewList in state or perform any necessary action here
    }
  };
  const [selectedOperationBalloonId, setSelectedOperationBalloonId] = useState<
    number | null
  >(null);
  const handleOperationStaffIdChange = (
    operationStaffId: number,
    crew?: IStaffAllocation,
  ) => {
    if (onOperationStaffIdChange) {
      onOperationStaffIdChange(operationStaffId); // Call the callback with the id
    }
    openModal('CrewForm', crew);
  };
  const [isModalOpenForDelete, setIsModalOpenForDelete] = useState(false);
  const [selectedOperationStaffId, setSelectedOperationStaffId] = useState<
    number | null
  >(null);
  const openDeleteModal = (
    operationStaffId: number,
    operationBalloonId: number,
  ) => {
    setSelectedOperationStaffId(operationStaffId);
    setSelectedOperationBalloonId(operationBalloonId);
    setIsModalOpenForDelete(true);
  };
  const closeDeleteModal = () => {
    setSelectedOperationStaffId(null);
    setSelectedOperationBalloonId(null);
    setIsModalOpenForDelete(false);
  };
  const confirmDeletion = async () => {
    try {
      if (!selectedOperationStaffId) {
        console.error('No operationStaffId selected for deletion.');
        return;
      }
      const response = await operationAPI.deleteStaff(
        selectedOperationStaffId,
        selectedOperationBalloonId,
      );
      console.log(response);
      toast.success(translate('driver.deleteStaff'));
      onStaffChange();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.warn(translate('operation.commonError'));
    }
  };

  return (
    <>
      {crewList.map((crew: IStaffAllocation, index) => {
        const vehicle = crewVehicleList.find(
          (vehicle) => vehicle.busId === crew.busId,
        );
        return (
          <div className="mt-4" key={crew.operationStaffId}>
            <div
              className={`bg-backgroundColor-white rounded-lg shadow-md p-5`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-textColor">
                  {crew.staffName}
                </h2>
                <div className="flex justify-between">
                  <button
                    className="text-textColor hover:text-textColor mr-4"
                    onClick={() =>
                      openDeleteModal(
                        crew?.operationStaffId,
                        crew?.operationBalloonId,
                      )
                    }
                  >
                    <div>
                      <div className="block dark:hidden">
                        <DeleteIcon />
                      </div>
                      <div className="hidden dark:block">
                        <DeleteIconDark />
                      </div>
                    </div>
                  </button>
                  <button
                    className="text-textColor hover:text-textColor"
                    onClick={() => {
                      toggleEditMode(index); // Toggle edit mode for this specific card
                      handleOperationStaffIdChange(crew.operationStaffId, crew);
                    }}
                  >
                    {/* Edit Icon */}
                    <div>
                      <div className="block dark:hidden">
                        <EditIcon />
                      </div>
                      <div className="hidden dark:block">
                        <EditIconDark />
                      </div>
                    </div>
                  </button>
                </div>
              </div>
              {/* Details Section */}
              <div className="grid sm:grid-cols-3 grid-cols-2  gap-y-2 text-start text-sm">
                {/* Each row */}

                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">
                    Qualification
                  </p>
                  {crew?.staffqualifications ? (
                    <>
                      <p className="text-textColorGray">
                        {' '}
                        {crew?.staffqualifications}
                      </p>
                    </>
                  ) : (
                    '---'
                  )}
                </div>

                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">Balloon</p>
                  <p className="text-textColorGray">{crew?.balloonName}</p>
                </div>

                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">Vehicle</p>
                  <p className="text-textColorGray">{vehicle?.busName}</p>
                </div>

                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">Phone Number</p>
                  <p className="text-textColorGray">
                    {['', 'null', 'undefined', null, undefined].includes(
                      crew?.resPhone
                    )
                      ? '---'
                      : crew?.resPhone}
                  </p>
                </div>
                <div className="flex flex-col"></div>
                <div className="flex flex-col"></div>
                <div className=" space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-textColor border-gray-300 rounded focus:ring-blue-400"
                      checked={crew?.preCheck}
                      onChange={() => handleCheckboxChange(index, 'preCheck')}
                      disabled={!editModes[index]} // Disable if not in edit mode
                    />
                    <span className="text-sm text-textColorGray">PreCheck</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-textColor border-gray-300 rounded focus:ring-blue-400"
                      checked={crew?.need}
                      onChange={() => handleCheckboxChange(index, 'need')}
                      disabled={!editModes[index]} // Disable if not in edit mode
                    />
                    <span className="text-sm text-textColorGray">Need</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-textColor border-gray-300 rounded focus:ring-blue-400"
                      checked={crew?.kkd}
                      onChange={() => handleCheckboxChange(index, 'kkd')}
                      disabled={!editModes[index]} // Disable if not in edit mode
                    />
                    <span className="text-sm text-textColorGray">KK'D</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {isModalOpenForDelete && (
        <DeleteConfirmation
          isOpen={isModalOpenForDelete}
          onClose={closeDeleteModal}
          onConfirm={confirmDeletion}
          itemName="Crew"
          operationStaffId={selectedOperationStaffId || undefined}
        />
      )}
    </>
  );
};

export default CrewCard;
